import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import api from '../services/api';
import Notification from '../components/Notification';
import { useAuth } from './auth';

const FavoritesContext = createContext({});

const FavoritesProvider = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Um único carregamento de ids por sessão alimenta o coração de todos os
  // cards; as listagens já trazem `favorited`, mas nem todas (destaques,
  // relacionados) passam pelo mesmo endpoint.
  const loadFavorites = useCallback(async () => {
    if (!isLoggedIn) {
      setFavoriteIds(new Set());
      return;
    }

    try {
      setIsLoading(true);
      const { data } = await api.get('/favorite_projects', {
        params: { limit: 200 },
      });
      setFavoriteIds(new Set(data.projects.map(p => p.id)));
    } catch {
      // Falha aqui só significa corações vazios; nada bloqueia a navegação.
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const isFavorite = useCallback(
    projectId => favoriteIds.has(Number(projectId)),
    [favoriteIds]
  );

  const toggleFavorite = useCallback(
    async projectId => {
      const id = Number(projectId);
      const wasFavorite = favoriteIds.has(id);

      // Otimista: o coração responde na hora e volta atrás se a API recusar.
      setFavoriteIds(current => {
        const next = new Set(current);
        if (wasFavorite) next.delete(id);
        else next.add(id);
        return next;
      });

      try {
        if (wasFavorite) {
          await api.delete(`/project/${id}/favorite`);
        } else {
          await api.post(`/project/${id}/favorite`);
        }
        return !wasFavorite;
      } catch {
        setFavoriteIds(current => {
          const next = new Set(current);
          if (wasFavorite) next.add(id);
          else next.delete(id);
          return next;
        });
        Notification({
          type: Notification.types.ERROR,
          message: 'Não foi possível atualizar seus favoritos',
        });
        return wasFavorite;
      }
    },
    [favoriteIds]
  );

  const value = useMemo(
    () => ({
      favoriteIds,
      favoritesCount: favoriteIds.size,
      isFavorite,
      toggleFavorite,
      reloadFavorites: loadFavorites,
      isLoading,
    }),
    [favoriteIds, isFavorite, toggleFavorite, loadFavorites, isLoading]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

function useFavorites() {
  const context = useContext(FavoritesContext);

  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }

  return context;
}

export { FavoritesProvider, useFavorites };
